<template>
  <div class="app-content pt-3 p-md-3 p-lg-4">
    <div class="container-fluid">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="app-page-title mb-0">Curriculum</h1>
        <div>
          <button class="btn btn-outline-secondary mr-2" @click="showUploadModal = true">
            <b-icon icon="cloud-upload" class="mr-1"></b-icon> Upload Excel
          </button>
          <button class="btn btn-primary" @click="openCreateModal">
            <b-icon icon="plus-circle" class="mr-1"></b-icon> Add to Curriculum
          </button>
        </div>
      </div>

      <div class="app-card shadow-sm mb-4">
        <div class="app-card-body p-4">
          <!-- Filters -->
          <div class="row mb-3">
            <div class="col-md-3">
              <v-select
                v-model="filters.programme_id"
                :options="programmes"
                :get-option-label="o => o.programme + ' (' + (o.code || '') + ')'"
                :reduce="o => o.id"
                :filter="filterProgrammes"
                placeholder="All Programmes"
                :clearable="true"
                @input="loadCurriculum"
              ></v-select>
            </div>
            <div class="col-md-2">
              <b-form-select
                v-model="filters.year"
                :options="yearOptions"
                @change="loadCurriculum"
              ></b-form-select>
            </div>
            <div class="col-md-2">
              <b-form-select
                v-model="filters.semester"
                :options="semesterOptions"
                @change="loadCurriculum"
              ></b-form-select>
            </div>
            <div class="col-md-3">
              <b-form-input
                v-model="filters.search"
                placeholder="Search course code..."
                debounce="300"
                @update="loadCurriculum"
              ></b-form-input>
            </div>
            <div class="col-md-2 text-right">
              <small class="text-muted" v-if="!loading">{{ curriculum.length }} entries</small>
            </div>
          </div>

          <b-table
            :items="curriculum"
            :fields="fields"
            :busy="loading"
            :per-page="perPage"
            :current-page="currentPage"
            responsive
            striped
            hover
            show-empty
          >
            <template #table-busy>
              <div class="text-center my-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="mt-2 text-muted">Loading curriculum...</div>
              </div>
            </template>
            <template #empty>
              <div class="text-center text-muted py-4">
                {{ filters.programme_id ? 'No curriculum entries found.' : 'Select a programme to view its curriculum.' }}
              </div>
            </template>
            <template #cell(index)="data">
              {{ (currentPage - 1) * perPage + data.index + 1 }}
            </template>
            <template #cell(course_status)="data">
              <b-badge :variant="data.value === 'C' ? 'primary' : 'warning'">
                {{ data.value === 'C' ? 'Compulsory' : 'Elective' }}
              </b-badge>
            </template>
            <template #cell(semester)="data">
              {{ data.value == 1 ? '1st' : '2nd' }}
            </template>
            <template #cell(course_unit)="data">
              <b-badge variant="info">{{ data.value }}</b-badge>
            </template>
            <template #cell(actions)="data">
              <button class="btn btn-sm btn-info mr-1" @click="openEditModal(data.item)" :disabled="deleting">
                <b-icon icon="pencil"></b-icon>
              </button>
              <button class="btn btn-sm btn-danger" @click="confirmDelete(data.item)" :disabled="deleting">
                <b-spinner small v-if="deletingId === data.item.id"></b-spinner>
                <b-icon icon="trash" v-else></b-icon>
              </button>
            </template>
          </b-table>

          <div class="d-flex justify-content-between align-items-center" v-if="curriculum.length > perPage">
            <small class="text-muted">Page {{ currentPage }} of {{ Math.ceil(curriculum.length / perPage) }}</small>
            <b-pagination
              v-model="currentPage"
              :total-rows="curriculum.length"
              :per-page="perPage"
              size="sm"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Create/Edit Modal -->
      <b-modal
        v-model="showModal"
        :title="isEditing ? 'Edit Curriculum Entry' : 'Add to Curriculum'"
        @ok.prevent="saveEntry"
        :ok-disabled="saving"
        no-close-on-backdrop
        size="lg"
      >
        <template #modal-ok>
          <b-spinner small v-if="saving" class="mr-1"></b-spinner>
          {{ saving ? 'Saving...' : 'Save' }}
        </template>
        <b-form @submit.prevent="saveEntry">
          <b-form-group label="Programme" label-for="cur-programme" v-if="!isEditing">
            <v-select
              v-model="form.programme_id"
              :options="programmes"
              :get-option-label="o => o.programme + ' (' + (o.code || '') + ')'"
              :reduce="o => o.id"
              :filter="filterProgrammes"
              :disabled="saving"
              placeholder="Select Programme"
            ></v-select>
          </b-form-group>
          <b-form-group :label="isEditing ? 'Course' : 'Course'" label-for="cur-course" v-if="!isEditing">
            <b-form-select
              id="cur-course"
              v-model="form.course_id"
              :options="courseSelectOptions"
              required
              :disabled="saving"
            >
            </b-form-select>
          </b-form-group>
          <div v-if="isEditing" class="mb-3">
            <strong>Course:</strong> {{ editingCourseLabel }}
          </div>
          <div class="row">
            <div class="col-md-4">
              <b-form-group label="Year" label-for="cur-year">
                <b-form-select
                  id="cur-year"
                  v-model="form.year"
                  :options="[{value:null,text:'Select Year'},{value:1,text:'Year 1'},{value:2,text:'Year 2'},{value:3,text:'Year 3'},{value:4,text:'Year 4'},{value:5,text:'Year 5'}]"
                  required
                  :disabled="saving"
                ></b-form-select>
              </b-form-group>
            </div>
            <div class="col-md-4">
              <b-form-group label="Semester" label-for="cur-semester">
                <b-form-select
                  id="cur-semester"
                  v-model="form.semester"
                  :options="[{value:null,text:'Select Semester'},{value:1,text:'1st Semester'},{value:2,text:'2nd Semester'}]"
                  required
                  :disabled="saving"
                ></b-form-select>
              </b-form-group>
            </div>
            <div class="col-md-4">
              <b-form-group label="Status" label-for="cur-status">
                <b-form-select
                  id="cur-status"
                  v-model="form.course_status"
                  :options="[{value:null,text:'Select Status'},{value:'C',text:'Compulsory'},{value:'E',text:'Elective'}]"
                  required
                  :disabled="saving"
                ></b-form-select>
              </b-form-group>
            </div>
          </div>
        </b-form>
      </b-modal>

      <!-- Upload Excel Modal -->
      <b-modal
        v-model="showUploadModal"
        title="Upload Curriculum (Excel)"
        @ok.prevent="uploadExcel"
        :ok-disabled="uploading || !uploadFile || !uploadProgrammeId"
        no-close-on-backdrop
      >
        <template #modal-ok>
          <b-spinner small v-if="uploading" class="mr-1"></b-spinner>
          {{ uploading ? 'Uploading...' : 'Upload' }}
        </template>
        <p class="text-muted small mb-3">
          Excel columns: <strong>Course Code</strong>, <strong>Status</strong> (C/E), <strong>Semester</strong> (1/2), <strong>Year</strong> (1-5). First row is header.
        </p>
        <b-form-group label="Programme">
          <v-select
            v-model="uploadProgrammeId"
            :options="programmes"
            :get-option-label="o => o.programme + ' (' + (o.code || '') + ')'"
            :reduce="o => o.id"
            :filter="filterProgrammes"
            :disabled="uploading"
            placeholder="Select Programme"
          ></v-select>
        </b-form-group>
        <b-form-group label="Select File">
          <b-form-file v-model="uploadFile" accept=".xlsx,.xls,.csv" placeholder="Choose a file..." :disabled="uploading"></b-form-file>
        </b-form-group>
        <b-progress v-if="uploadProgress > 0" :value="uploadProgress" max="100" show-progress animated class="mt-2"></b-progress>
      </b-modal>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CurriculumManagement',
  data() {
    return {
      curriculum: [],
      programmes: [],
      courses: [],
      loading: false,
      filters: {
        programme_id: null,
        year: null,
        semester: null,
        search: '',
      },
      currentPage: 1,
      perPage: 25,
      fields: [
        { key: 'index', label: '#', sortable: false },
        { key: 'course_code', label: 'Code', sortable: true },
        { key: 'course_description', label: 'Description', sortable: true },
        { key: 'course_unit', label: 'Unit', sortable: true },
        { key: 'programme_name', label: 'Programme', sortable: true },
        { key: 'year', label: 'Year', sortable: true },
        { key: 'semester', label: 'Semester', sortable: true },
        { key: 'course_status', label: 'Status', sortable: true },
        { key: 'actions', label: 'Actions', sortable: false },
      ],
      showModal: false,
      isEditing: false,
      editingId: null,
      editingCourseLabel: '',
      saving: false,
      form: { programme_id: null, course_id: null, year: null, semester: null, course_status: null },
      deleting: false,
      deletingId: null,
      showUploadModal: false,
      uploading: false,
      uploadFile: null,
      uploadProgrammeId: null,
      uploadProgress: 0,
    };
  },
  computed: {
    programmeOptions() {
      return [
        { value: null, text: 'All Programmes' },
        ...this.programmes.map(p => ({ value: p.id, text: p.programme + ' (' + p.code + ')' }))
      ];
    },
    programmeSelectOptions() {
      return [
        { value: null, text: 'Select Programme', disabled: true },
        ...this.programmes.map(p => ({ value: p.id, text: p.programme + ' (' + p.code + ')' }))
      ];
    },
    courseSelectOptions() {
      return [
        { value: null, text: 'Select Course', disabled: true },
        ...this.courses.map(c => ({ value: c.id, text: c.code + ' - ' + c.description + ' (' + c.unit + ' units)' }))
      ];
    },
    yearOptions() {
      return [
        { value: null, text: 'All Years' },
        { value: 1, text: 'Year 1' },
        { value: 2, text: 'Year 2' },
        { value: 3, text: 'Year 3' },
        { value: 4, text: 'Year 4' },
        { value: 5, text: 'Year 5' },
      ];
    },
    semesterOptions() {
      return [
        { value: null, text: 'All Semesters' },
        { value: 1, text: '1st Semester' },
        { value: 2, text: '2nd Semester' },
      ];
    },
  },
  created() {
    this.loadProgrammes();
    this.loadCoursesForSelect();
  },
  methods: {
    loadProgrammes() {
      axios.get('/admin/programmes').then(res => {
        this.programmes = res.data.data || [];
      });
    },
    filterProgrammes(options, search) {
      var s = search.toLowerCase();
      return options.filter(function (o) {
        return (o.programme || '').toLowerCase().indexOf(s) > -1
          || (o.code || '').toLowerCase().indexOf(s) > -1
          || (o.degree || '').toLowerCase().indexOf(s) > -1;
      });
    },
    loadCoursesForSelect() {
      axios.get('/admin/courses').then(res => {
        this.courses = res.data.courses || [];
      });
    },
    loadCurriculum() {
      this.loading = true;
      this.currentPage = 1;
      const params = {};
      if (this.filters.programme_id) params.programme_id = this.filters.programme_id;
      if (this.filters.year) params.year = this.filters.year;
      if (this.filters.semester) params.semester = this.filters.semester;
      if (this.filters.search) params.search = this.filters.search;

      axios.get('/admin/curriculum', { params })
        .then(res => { this.curriculum = res.data.curriculum || []; })
        .catch(() => { Toast.fire({ icon: 'error', title: 'Failed to load curriculum' }); })
        .finally(() => { this.loading = false; });
    },
    openCreateModal() {
      this.isEditing = false;
      this.editingId = null;
      this.form = {
        programme_id: this.filters.programme_id || null,
        course_id: null,
        year: null,
        semester: null,
        course_status: null,
      };
      this.showModal = true;
    },
    openEditModal(item) {
      this.isEditing = true;
      this.editingId = item.id;
      this.editingCourseLabel = item.course_code + ' - ' + item.course_description;
      this.form = {
        programme_id: item.programme_id,
        course_id: item.course_id,
        year: item.year,
        semester: item.semester,
        course_status: item.course_status,
      };
      this.showModal = true;
    },
    saveEntry() {
      if (!this.isEditing && (!this.form.programme_id || !this.form.course_id)) {
        Toast.fire({ icon: 'warning', title: 'Select a programme and course' });
        return;
      }
      if (!this.form.year || !this.form.semester || !this.form.course_status) {
        Toast.fire({ icon: 'warning', title: 'All fields are required' });
        return;
      }
      this.saving = true;

      const request = this.isEditing
        ? axios.put('/admin/curriculum/' + this.editingId, this.form)
        : axios.post('/admin/curriculum', this.form);

      request
        .then(res => {
          Toast.fire({ icon: 'success', title: res.data.message || 'Saved' });
          this.showModal = false;
          this.loadCurriculum();
        })
        .catch(err => {
          const msg = err.response && err.response.data && (err.response.data.message || err.response.data.error);
          Toast.fire({ icon: 'error', title: msg || 'Failed to save' });
        })
        .finally(() => { this.saving = false; });
    },
    confirmDelete(item) {
      Swal.fire({
        title: 'Remove from Curriculum?',
        html: `Remove <strong>${item.course_code}</strong> from <strong>${item.programme_name}</strong>?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, remove',
      }).then(result => {
        if (result.isConfirmed) this.deleteEntry(item);
      });
    },
    deleteEntry(item) {
      this.deleting = true;
      this.deletingId = item.id;
      axios.delete('/admin/curriculum/' + item.id)
        .then(res => {
          Toast.fire({ icon: 'success', title: res.data.message || 'Deleted' });
          this.loadCurriculum();
        })
        .catch(err => {
          const msg = err.response && err.response.data && (err.response.data.message || err.response.data.error);
          Toast.fire({ icon: 'error', title: msg || 'Failed to delete' });
        })
        .finally(() => { this.deleting = false; this.deletingId = null; });
    },
    uploadExcel() {
      if (!this.uploadFile || !this.uploadProgrammeId) return;
      this.uploading = true;
      this.uploadProgress = 0;

      let formData = new FormData();
      formData.append('curriculum', this.uploadFile);
      formData.append('programme_', this.uploadProgrammeId);

      axios.post('/admin/upload/curriculum', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          this.uploadProgress = parseInt(Math.round((e.loaded / e.total) * 100));
        },
      })
        .then(() => {
          Toast.fire({ icon: 'success', title: 'Curriculum uploaded successfully' });
          this.showUploadModal = false;
          this.uploadFile = null;
          this.uploadProgrammeId = null;
          this.uploadProgress = 0;
          this.loadCurriculum();
        })
        .catch(() => {
          Toast.fire({ icon: 'error', title: 'Error uploading curriculum' });
        })
        .finally(() => { this.uploading = false; });
    },
  },
};
</script>
